import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../services/auth.service';
import { EnvironmentService } from '../../services/environment.service';
import { LocalizationService } from '../../services/localization.service';
import { NotificationsService } from '../../services/notifications.service';
import { WebPushService } from '../../services/web-push.service';
import { UserProfileNotificationChannelsData, UserProfileNotificationData, UserProfileNotificationEventData, UserProfileNotificationTypesData } from '../../services/data.service';
import { PgSelectOption } from '../../pg-ui-controls/pg-select/pg-select.component';

@Component({
    selector: 'app-pg-manage-notifications',
    templateUrl: './pg-manage-notifications.component.html',
    styleUrls: ['./pg-manage-notifications.component.scss'],
    standalone: false
})
export class PgManageNotificationsComponent implements OnInit {

    constructor(private activeModal:NgbActiveModal, private authService:AuthService, private environmentService:EnvironmentService, private localizationService:LocalizationService, private notificationsService:NotificationsService, public webPushService:WebPushService) { }

    isSavingNotificationSettings = false;
    isTogglingWebPush = false;
    showOtherRealmsSwitch = false;

    manageNotificationData:UserProfileNotificationData = null;

    notificationChannelOptions:Array<PgSelectOption> = []
    notificationTypeOptions:Array<PgSelectOption> = []
    notificationDigestOptions:Array<PgSelectOption> = []

    eventKeys = UserProfileNotificationData.EVENT_KEYS
    eventKeysWithDigest = UserProfileNotificationData.EVENT_KEYS_WITH_DIGEST

    private static readonly TYPE_RESOURCE_LABELS:{ [key in UserProfileNotificationTypesData]: string } = {
        eatery: 'Eatery',
        host: 'Host',
        experiencesupplier: 'ExperienceSupplier',
        experience: 'Experience',
        article: 'Article',
        poi: 'Poi',
        itinerary: 'Itinerary',
    }

    ngOnInit(): void {
        this._initNotificationOptions()
        this.showOtherRealmsSwitch = this._hasOtherRealmsNotificationScope()
        this.manageNotificationData = new UserProfileNotificationData(this.authService.profile?.notification_settings)

        let _areChannelsEmpty = true;

        if(this.manageNotificationData.channels != null) {
            for(let _channel of UserProfileNotificationData.CHANNEL_KEYS) {
                if(this.manageNotificationData.channels[_channel] != null) {
                    _areChannelsEmpty = false;
                    break;
                }
            }
        }

        if(_areChannelsEmpty) {
            let _hasEmail = this.authService.user?.email != null && this.authService.user.email != ''
            let _hasTelephone = this.authService.user?.telephone != null && this.authService.user.telephone != ''

            this.manageNotificationData.channels = {
                in_app: true,
                email: _hasEmail,
                sms: !_hasEmail && _hasTelephone,
                push: true,
            }
        }
    }

    private _initNotificationOptions() {
        this.notificationChannelOptions = UserProfileNotificationData.CHANNEL_KEYS.map((_channel) => ({
            value: _channel,
            text: this.localizationService.translate('pg-user-menu.modal-manage-notifications-channel-' + _channel)
        }))

        this.notificationTypeOptions = UserProfileNotificationData.TYPE_KEYS.map((_type) => ({
            value: _type,
            text: this.localizationService.translateResource(PgManageNotificationsComponent.TYPE_RESOURCE_LABELS[_type])
        }))

        this.notificationDigestOptions = [60, 180, 360, 720, 1440].map((_minutes) => ({
            value: String(_minutes),
            text: this.localizationService.translate('pg-user-menu.modal-manage-notifications-digest-' + _minutes)
        }))
    }

    private _hasOtherRealmsNotificationScope() {
        let _selectableRealms = this.authService.user?.getSelectableRealms()
        if(_selectableRealms == null) return false;

        for(let _userRealm of _selectableRealms) {
            if(_userRealm?.id == null) continue;

            let _scope = this.environmentService.getRealm(_userRealm.id)?.configuration?.content_notification?.scope
            if(_scope == 'installation' || _scope == 'multi_realm') {
                return true;
            }
        }

        return false;
    }

    getEventLabel(eventType:string) {
        return this.localizationService.getNotificationEventLabel(eventType)
    }

    getChannelsValue() {
        let _channels:UserProfileNotificationChannelsData = {}

        for(let _channel of UserProfileNotificationData.CHANNEL_KEYS) {
            _channels[_channel] = this.manageNotificationData?.channels?.[_channel] === true
        }

        return JSON.stringify(_channels)
    }

    setChannelsValue(val:string) {
        if(this.manageNotificationData == null) return;

        let _parsed:UserProfileNotificationChannelsData = {}

        if(val != null && val != '' && val != '{}') {
            try {
                _parsed = JSON.parse(val)
            }
            catch(ex) {
                _parsed = {}
            }
        }

        let _channels:UserProfileNotificationChannelsData = {}

        for(let _channel of UserProfileNotificationData.CHANNEL_KEYS) {
            _channels[_channel] = _parsed[_channel] === true
        }

        this.manageNotificationData.channels = _channels
    }

    getTypesValue() {
        let _types:{ [key in UserProfileNotificationTypesData]?: boolean } = {}

        for(let _type of UserProfileNotificationData.TYPE_KEYS) {
            _types[_type] = this.manageNotificationData?.type?.[_type] !== false
        }

        return JSON.stringify(_types)
    }

    setTypesValue(val:string) {
        if(this.manageNotificationData == null) return;

        let _parsed:{ [key in UserProfileNotificationTypesData]?: boolean } = {}

        if(val != null && val != '' && val != '{}') {
            try {
                _parsed = JSON.parse(val)
            }
            catch(ex) {
                _parsed = {}
            }
        }

        let _types:{ [key in UserProfileNotificationTypesData]?: boolean } = {}

        for(let _type of UserProfileNotificationData.TYPE_KEYS) {
            if(_parsed[_type] === false) {
                _types[_type] = false
            }
        }

        this.manageNotificationData.type = _types
    }

    getEventEnabled(eventType:string) {
        return this.manageNotificationData?.events?.[eventType]?.enabled !== false
    }

    setEventEnabled(eventType:string, enabled:boolean) {
        if(this.manageNotificationData == null) return;

        if(this.manageNotificationData.events == null) {
            this.manageNotificationData.events = {}
        }

        if(enabled) {
            delete this.manageNotificationData.events[eventType]
        }
        else {
            let _event = new UserProfileNotificationEventData()
            _event.enabled = false
            this.manageNotificationData.events[eventType] = _event
        }
    }

    getDigestMinutes() {
        let _minutes = this.manageNotificationData?.digest_minutes
        return _minutes == null ? '360' : String(_minutes)
    }

    setDigestMinutes(val:string) {
        if(this.manageNotificationData == null) return;

        this.manageNotificationData.digest_minutes = val == null || val == '' ? 360 : parseInt(val, 10)
    }

    getDigestUnifyProductTypes() {
        return this.manageNotificationData?.digest_unify_product_types === true
    }

    setDigestUnifyProductTypes(val:boolean) {
        if(this.manageNotificationData == null) return;

        this.manageNotificationData.digest_unify_product_types = val === true
    }

    getOtherRealms() {
        return this.manageNotificationData?.other_realms !== false
    }

    setOtherRealms(val:boolean) {
        if(this.manageNotificationData == null) return;

        this.manageNotificationData.other_realms = val === true
    }

    async setDeviceWebPushEnabled(enabled:boolean) {
        if(this.isTogglingWebPush || !this.webPushService.isSupported()) return;

        this.isTogglingWebPush = true;

        try {
            if(enabled) {
                await this.webPushService.enable()
            }
            else {
                await this.webPushService.disable()
            }
        }
        finally {
            this.isTogglingWebPush = false
        }
    }

    dismissModal() {
        this.activeModal.dismiss();
    }

    private _getNotificationSettingsForSave() {
        let _result = new UserProfileNotificationData()

        _result.digest_minutes = this.manageNotificationData.digest_minutes == null ? 360 : this.manageNotificationData.digest_minutes
        _result.digest_unify_product_types = this.manageNotificationData.digest_unify_product_types === true
        _result.other_realms = this.manageNotificationData.other_realms !== false

        for(let _type of UserProfileNotificationData.TYPE_KEYS) {
            if(this.manageNotificationData.type?.[_type] === false) {
                _result.type[_type] = false
            }
        }

        for(let _channel of UserProfileNotificationData.CHANNEL_KEYS) {
            _result.channels[_channel] = this.manageNotificationData.channels?.[_channel] === true
        }

        for(let _eventType of [...UserProfileNotificationData.EVENT_KEYS, ...UserProfileNotificationData.EVENT_KEYS_WITH_DIGEST]) {
            if(this.manageNotificationData.events?.[_eventType]?.enabled === false) {
                _result.events[_eventType] = { enabled: false }
            }
        }

        return _result
    }

    doSave() {
        if(this.isSavingNotificationSettings || this.manageNotificationData == null) return;

        this.isSavingNotificationSettings = true;

        this.authService.setUserProfile({
            notification_settings: this._getNotificationSettingsForSave()
        }).then(() => {
            this.isSavingNotificationSettings = false;
            this.notificationsService.addLocalNotification(this.localizationService.translate('pg-user-menu.modal-manage-notifications-save-successful'), 'success', null, null, 3000)
            this.activeModal.close();
        }, () => {
            this.isSavingNotificationSettings = false;
        })
    }
}
